import { useEffect, useMemo } from "react";

import { create } from "zustand";

const getDimensions = ({ excludeScrollbar }) => ({
  width: excludeScrollbar
    ? window.document.documentElement.clientWidth
    : window.innerWidth,
  height: excludeScrollbar
    ? window.document.documentElement.clientHeight
    : window.innerHeight,
});

const windowSizeStore = create((set) => ({
  windowSize: getDimensions({ excludeScrollbar: false }),
  setWindowSize: (newWindowSize) => set({ windowSize: newWindowSize }),
  windowSizeWithoutScrollbar: getDimensions({ excludeScrollbar: true }),
  setWindowSizeWithoutScrollbar: (newWindowSizeWithoutScrollbar) =>
    set({ windowSizeWithoutScrollbar: newWindowSizeWithoutScrollbar }),
  windowRef: { current: window.document.body },
  isRegisteredRef: { current: false },
  animationRef: { current: false },
}));

/**
 * @param {object} param0
 * @param {boolean} param0.excludeScrollbar
 * @returns {{ width: number, height: number }}
 */
export function useWindowSize({ excludeScrollbar } = {}) {
  const windowSize = windowSizeStore((s) => s.windowSize);
  const setWindowSize = windowSizeStore((s) => s.setWindowSize);
  const windowSizeWithoutScrollbar = windowSizeStore(
    (s) => s.windowSizeWithoutScrollbar
  );
  const setWindowSizeWithoutScrollbar = windowSizeStore(
    (s) => s.setWindowSizeWithoutScrollbar
  );
  const isRegisteredRef = windowSizeStore((s) => s.isRegisteredRef);
  const animationRef = windowSizeStore((s) => s.animationRef);

  useEffect(() => {
    // Only register listener once across the whole app.
    if (isRegisteredRef.current) return;

    const applyDimensions = () => {
      setWindowSize(getDimensions({ excludeScrollbar: false }));
      setWindowSizeWithoutScrollbar(
        getDimensions({ excludeScrollbar: true })
      );
    };

    const updateSize = () => {
      if (animationRef.current) return;
      animationRef.current = requestAnimationFrame(() => {
        applyDimensions();
        animationRef.current = undefined;
      });
    };

    window.addEventListener("resize", updateSize);
    window.visualViewport?.addEventListener("resize", applyDimensions);
    isRegisteredRef.current = true;
  }, [
    animationRef,
    isRegisteredRef,
    setWindowSize,
    setWindowSizeWithoutScrollbar,
  ]);

  return useMemo(
    () => (excludeScrollbar ? windowSizeWithoutScrollbar : windowSize),
    [excludeScrollbar, windowSize, windowSizeWithoutScrollbar]
  );
}
